import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { humanDate } from "@/lib/dates";
import TaskEditDialog from "@/components/TaskEditDialog";
import KebabMenu from "@/components/KebabMenu";
import { toast } from "sonner";
import {
  PauseCircle, Loader2, RotateCcw, Bell, Filter as FilterIcon,
  PackageCheck, FileText, Calendar as CalendarIcon, ChevronRight,
  CheckCircle2, ArrowRight,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const CATEGORY_LABEL = {
  odgovor: "Odgovor", odluka: "Odluka", odobrenje: "Odobrenje",
  dokument: "Dokument", ponuda: "Ponuda", informacija: "Informacija",
  isporuka: "Isporuka", uplata: "Uplata", ostalo: "Ostalo",
};

const TONE_BG = {
  critical: "bg-terracotta/12 border-terracotta/25 text-terracotta",
  warning: "bg-ochre/15 border-ochre/30 text-ochre-ink",
  neutral: "bg-surface-secondary border-line text-ink-muted",
};

function browserTZ() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Belgrade"; }
  catch { return "Europe/Belgrade"; }
}

function initials(name) {
  return (name || "?").split(/\s+/).slice(0, 2).map(s => s[0]).filter(Boolean).join("").toUpperCase();
}

function dominantSignal(task, today) {
  if (task.wait_overdue) return { text: "Čekanje isteklo", tone: "critical" };
  if (task.due_overdue) {
    const due = new Date(task.due_date);
    const days = Math.max(1, Math.round((new Date(today) - due) / 86400000));
    return { text: `Kasni ${days} ${days === 1 ? "dan" : "dana"}`, tone: "critical" };
  }
  const w = task.waiting || {};
  if (w.expected_date === today) return { text: "Očekivano danas", tone: "warning" };
  if (task.waiting_days >= 1) {
    const d = task.waiting_days;
    return { text: `Čeka ${d} ${d === 1 ? "dan" : "dana"}`, tone: "warning" };
  }
  return { text: "Čeka", tone: "warning" };
}

export default function Waiting() {
  const [state, setState] = useState({ status: "loading", tasks: [], today: null, error: null });
  const [collabs, setCollabs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [primary, setPrimary] = useState("all"); // Sve | Kasni | Ove nedelje
  const [advFilters, setAdvFilters] = useState({
    category: null, personId: null, projectId: null, expectedBefore: null, priority: null,
  });
  const [advOpen, setAdvOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [arriveTarget, setArriveTarget] = useState(null);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [reminderNote, setReminderNote] = useState("");
  const [expectedTarget, setExpectedTarget] = useState(null);
  const [expectedDate, setExpectedDate] = useState("");
  const [statusTarget, setStatusTarget] = useState(null);
  const tz = useMemo(browserTZ, []);

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const [w, c, p] = await Promise.all([
        api.get(`/dashboard/waiting-view?tz=${encodeURIComponent(tz)}`),
        api.get("/collaborators"),
        api.get("/projects"),
      ]);
      setCollabs(c.data);
      setProjects(p.data);
      setState({ status: "ready", tasks: w.data.tasks || [], today: w.data.local_date, error: null });
    } catch (e) {
      setState({ status: "error", tasks: [], today: null, error: formatApiError(e) });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const collabById = useMemo(() => Object.fromEntries(collabs.map((c) => [c.id, c])), [collabs]);
  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  const weekBounds = useMemo(() => {
    if (!state.today) return { start: null, end: null };
    const d = new Date(state.today);
    const dayIdx = (d.getDay() + 6) % 7; // Monday = 0
    const start = new Date(d); start.setDate(d.getDate() - dayIdx);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    const iso = (x) => x.toISOString().slice(0, 10);
    return { start: iso(start), end: iso(end) };
  }, [state.today]);

  const advCount = Object.values(advFilters).filter(Boolean).length;

  const filtered = useMemo(() => {
    let out = state.tasks;
    if (primary === "late") out = out.filter((t) => t.wait_overdue || t.due_overdue);
    else if (primary === "this_week") {
      out = out.filter((t) => {
        const exp = (t.waiting || {}).expected_date;
        return (exp && exp >= weekBounds.start && exp <= weekBounds.end) ||
               (t.due_date && t.due_date >= weekBounds.start && t.due_date <= weekBounds.end);
      });
    }
    if (advFilters.category) out = out.filter((t) => (t.waiting || {}).category === advFilters.category);
    if (advFilters.personId) {
      out = out.filter((t) => (t.waiting || {}).waiting_on_collaborator_id === advFilters.personId ||
                              (t.waiting || {}).waiting_on_external === advFilters.personId);
    }
    if (advFilters.projectId) out = out.filter((t) => t.project_id === advFilters.projectId);
    if (advFilters.expectedBefore) out = out.filter((t) => (t.waiting || {}).expected_date && (t.waiting || {}).expected_date <= advFilters.expectedBefore);
    if (advFilters.priority) out = out.filter((t) => t.priority === advFilters.priority);
    return out;
  }, [state.tasks, primary, weekBounds, advFilters]);

  const primaryFilters = [
    { key: "all", label: "Sve", count: state.tasks.length },
    { key: "late", label: "Kasni", count: state.tasks.filter((t) => t.wait_overdue || t.due_overdue).length },
    { key: "this_week", label: "Ove nedelje", count: state.tasks.filter((t) => {
      const exp = (t.waiting || {}).expected_date;
      return (exp && exp >= weekBounds.start && exp <= weekBounds.end);
    }).length },
  ];

  const markArrived = async (newStatus) => {
    if (!arriveTarget) return;
    try {
      await api.patch(`/tasks/${arriveTarget.id}`, { status: newStatus });
      toast.success("Status ažuriran");
      setArriveTarget(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReminder = async () => {
    if (!reminderTarget) return;
    try {
      await api.post(`/tasks/${reminderTarget.id}/reminder`, { note: reminderNote || null });
      toast.success("Podsetnik evidentiran (poruka NIJE poslata)");
      setReminderTarget(null);
      setReminderNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitExpected = async () => {
    if (!expectedTarget) return;
    try {
      const w = { ...(expectedTarget.waiting || {}), expected_date: expectedDate || null };
      await api.patch(`/tasks/${expectedTarget.id}`, { waiting: w });
      toast.success("Očekivani datum ažuriran");
      setExpectedTarget(null); setExpectedDate("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (newStatus) => {
    if (!statusTarget) return;
    try {
      await api.patch(`/tasks/${statusTarget.id}`, { status: newStatus });
      toast.success("Status ažuriran");
      setStatusTarget(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Unique people/projects for adv filter dropdowns
  const uniquePeople = useMemo(() => {
    const seen = new Map();
    state.tasks.forEach((t) => {
      const w = t.waiting || {};
      if (w.waiting_on_collaborator_id) {
        const c = collabById[w.waiting_on_collaborator_id];
        if (c) seen.set(c.id, c.name);
      } else if (w.waiting_on_external) {
        seen.set(w.waiting_on_external, w.waiting_on_external);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [state.tasks, collabById]);
  const uniqueProjects = useMemo(() => {
    const ids = new Set(state.tasks.map((t) => t.project_id).filter(Boolean));
    return Array.from(ids).map((id) => ({ id, name: projectById[id]?.name || "—" }));
  }, [state.tasks, projectById]);

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="waiting-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">
          Čekam
        </h1>
        <p className="text-ink-muted mt-1.5 text-sm max-w-lg">
          Sve stavke u statusu „Čeka" — bez obzira na to da li ih ti duguješ ili duguju tebi.
        </p>
      </header>

      {/* Filter row: 3 primary pills + Filteri (n) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="waiting-filters">
        {primaryFilters.map((f) => {
          const on = primary === f.key;
          return (
            <button
              key={f.key}
              data-testid={`waiting-filter-${f.key}`}
              onClick={() => setPrimary(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm transition-colors ${
                on ? "bg-forest text-white border-forest" : "bg-white text-ink border-line hover:bg-surface-secondary"
              }`}
            >
              {f.label}
              <span className={`text-xs tabular-nums ${on ? "opacity-85" : "text-ink-muted"}`}>({f.count})</span>
            </button>
          );
        })}
        <Popover open={advOpen} onOpenChange={setAdvOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="waiting-more-filters"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm ml-auto ${
                advCount > 0 ? "bg-forest text-white border-forest" : "bg-white text-ink border-line hover:bg-surface-secondary"
              }`}
            >
              <FilterIcon className="w-3.5 h-3.5" />
              Filteri{advCount > 0 ? ` (${advCount})` : ""}
            </button>
          </PopoverTrigger>
          <PopoverContent className="bg-white w-72" align="end" data-testid="waiting-adv-filters">
            <div className="space-y-3">
              <div>
                <Label>Kategorija</Label>
                <Select
                  value={advFilters.category || "any"}
                  onValueChange={(v) => setAdvFilters((s) => ({ ...s, category: v === "any" ? null : v }))}
                >
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Sve" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="any">Sve</SelectItem>
                    {Object.entries(CATEGORY_LABEL).map(([v, lb]) =>
                      <SelectItem key={v} value={v}>{lb}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Od osobe / kompanije</Label>
                <Select
                  value={advFilters.personId || "any"}
                  onValueChange={(v) => setAdvFilters((s) => ({ ...s, personId: v === "any" ? null : v }))}
                >
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Sve" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="any">Sve</SelectItem>
                    {uniquePeople.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Projekat</Label>
                <Select
                  value={advFilters.projectId || "any"}
                  onValueChange={(v) => setAdvFilters((s) => ({ ...s, projectId: v === "any" ? null : v }))}
                >
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Svi" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="any">Svi</SelectItem>
                    {uniqueProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Očekivano do</Label>
                <Input type="date" value={advFilters.expectedBefore || ""}
                       onChange={(e) => setAdvFilters((s) => ({ ...s, expectedBefore: e.target.value || null }))} />
              </div>
              <div>
                <Label>Prioritet</Label>
                <Select
                  value={advFilters.priority || "any"}
                  onValueChange={(v) => setAdvFilters((s) => ({ ...s, priority: v === "any" ? null : v }))}
                >
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Svi" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="any">Svi</SelectItem>
                    <SelectItem value="visok">Visok</SelectItem>
                    <SelectItem value="srednji">Srednji</SelectItem>
                    <SelectItem value="nizak">Nizak</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-1">
                <button className="btn-ghost !py-1.5 flex-1" onClick={() => setAdvFilters({ category: null, personId: null, projectId: null, expectedBefore: null, priority: null })}>
                  Poništi
                </button>
                <button className="btn-primary !py-1.5 flex-1" onClick={() => setAdvOpen(false)}>
                  Primeni
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* States */}
      {state.status === "loading" && (
        <div className="surface-card p-10 text-center text-ink-muted" data-testid="waiting-loading">
          <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
          Učitavam listu...
        </div>
      )}
      {state.status === "error" && (
        <div className="surface-card p-6 text-terracotta" data-testid="waiting-error">
          Greška: {state.error}
          <button className="ml-3 btn-ghost !py-1.5" onClick={load}>
            <RotateCcw className="w-4 h-4" /> Pokušaj ponovo
          </button>
        </div>
      )}
      {state.status === "ready" && filtered.length === 0 && (
        <div className="surface-card p-10 text-center text-ink-muted" data-testid="waiting-empty">
          <PauseCircle className="w-8 h-8 mx-auto text-ink-muted mb-2" />
          Ništa se ne čeka po ovim filterima.
        </div>
      )}

      {/* List */}
      {state.status === "ready" && filtered.length > 0 && (
        <ul className="surface-card divide-y divide-line" data-testid="waiting-list">
          {filtered.map((t) => (
            <WaitingRow
              key={t.id}
              task={t}
              today={state.today}
              collabById={collabById}
              onOpen={() => setEditing(t)}
              onArrive={() => setArriveTarget(t)}
              onRemind={() => { setReminderTarget(t); setReminderNote(""); }}
              onChangeExpected={() => { setExpectedTarget(t); setExpectedDate((t.waiting || {}).expected_date || ""); }}
              onChangeStatus={() => setStatusTarget(t)}
            />
          ))}
        </ul>
      )}

      {editing && (
        <TaskEditDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          task={editing}
          collaborators={collabs}
          onChanged={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}

      {/* Arrive */}
      <Dialog open={!!arriveTarget} onOpenChange={(v) => !v && setArriveTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="arrive-dialog">
          <DialogHeader><DialogTitle className="font-heading">Šta sada?</DialogTitle></DialogHeader>
          <div className="text-sm text-ink-muted">
            Stiglo je ono što si čekao za „{arriveTarget?.title}". Task ne završavamo automatski.
          </div>
          <div className="flex flex-col gap-2 mt-3">
            <button className="btn-ghost !justify-start" data-testid="arrive-open" onClick={() => markArrived("open")}>
              <RotateCcw className="w-4 h-4" /> Vrati u „Otvoren"
            </button>
            <button className="btn-ghost !justify-start" data-testid="arrive-in-progress" onClick={() => markArrived("in_progress")}>
              <ArrowRight className="w-4 h-4" /> Prebaci u „U toku"
            </button>
            <button className="btn-ghost !justify-start !text-forest" data-testid="arrive-done" onClick={() => markArrived("done")}>
              <CheckCircle2 className="w-4 h-4" /> Označi kao završen
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reminder */}
      <Dialog open={!!reminderTarget} onOpenChange={(v) => !v && setReminderTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Bell className="w-4 h-4" /> Evidentiraj podsetnik
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-ink-muted -mt-1">
            Sistem samo beleži datum i belešku — poruka <b>nije</b> poslata.
          </div>
          <div className="space-y-2 mt-3">
            <Label htmlFor="reminder-note">Beleška (opciono)</Label>
            <Textarea
              id="reminder-note" data-testid="reminder-note" rows={3}
              value={reminderNote}
              onChange={(e) => setReminderNote(e.target.value)}
              placeholder="npr. Pozvao ga u 15h, obećao je do sutra"
            />
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setReminderTarget(null)}>Otkaži</button>
            <button className="btn-primary" onClick={submitReminder} data-testid="reminder-save">Sačuvaj</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expected date */}
      <Dialog open={!!expectedTarget} onOpenChange={(v) => !v && setExpectedTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="expected-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" /> Promeni očekivani datum
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <Label htmlFor="exp-date">Očekivano</Label>
            <Input id="exp-date" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setExpectedTarget(null)}>Otkaži</button>
            <button className="btn-primary" onClick={submitExpected} data-testid="expected-save">Sačuvaj</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change status */}
      <Dialog open={!!statusTarget} onOpenChange={(v) => !v && setStatusTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="status-dialog">
          <DialogHeader><DialogTitle className="font-heading">Promeni status</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            {[
              { v: "open", label: "Otvoren" },
              { v: "in_progress", label: "U toku" },
              { v: "waiting", label: "Čeka" },
              { v: "blocked", label: "Blokiran" },
              { v: "done", label: "Završen" },
            ].map((s) => (
              <button key={s.v} className="btn-ghost !justify-start" data-testid={`status-${s.v}`} onClick={() => changeStatus(s.v)}>
                {s.label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WaitingRow({ task, today, collabById, onOpen, onArrive, onRemind, onChangeExpected, onChangeStatus }) {
  const w = task.waiting || {};
  const onWhomName = (w.waiting_on_collaborator_id && collabById[w.waiting_on_collaborator_id]?.name) ||
                     w.waiting_on_external || "—";
  const signal = dominantSignal(task, today);

  return (
    <li className="p-3 md:p-4" data-testid={`waiting-row-${task.id}`}>
      <button
        onClick={onOpen}
        className="w-full text-left flex items-start gap-3 min-w-0"
        data-testid={`waiting-open-${task.id}`}
      >
        <span
          className="mt-0.5 grid place-items-center w-10 h-10 rounded-full flex-shrink-0 bg-forest/10 text-forest text-xs font-semibold"
          aria-hidden
        >
          {initials(onWhomName)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs text-ink-muted truncate">{onWhomName}</div>
              <div className="text-sm md:text-base font-medium text-ink leading-snug break-words">
                {task.title}
              </div>
            </div>
            {signal && (
              <span className={"text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border flex-shrink-0 whitespace-nowrap " + (TONE_BG[signal.tone] || TONE_BG.neutral)}>
                {signal.text}
              </span>
            )}
          </div>
        </div>
      </button>

      <div className="mt-2.5 pl-[52px] flex items-center gap-2">
        <button
          data-testid={`arrive-btn-${task.id}`}
          onClick={onArrive}
          className="btn-primary !py-1.5 !text-xs"
        >
          <PackageCheck className="w-3.5 h-3.5" /> Stiglo je
        </button>
        <KebabMenu
          testid={`row-kebab-${task.id}`}
          items={[
            { label: "Evidentiraj podsetnik", icon: <Bell className="w-4 h-4" />, onClick: onRemind, testid: `remind-btn-${task.id}` },
            { label: "Promeni očekivani datum", icon: <CalendarIcon className="w-4 h-4" />, onClick: onChangeExpected, testid: `expected-btn-${task.id}` },
            { label: "Promeni status", icon: <ChevronRight className="w-4 h-4" />, onClick: onChangeStatus, testid: `status-btn-${task.id}` },
            { label: "Otvori task", icon: <FileText className="w-4 h-4" />, onClick: onOpen, testid: `edit-btn-${task.id}`, separatorBefore: true },
          ]}
        />
      </div>
    </li>
  );
}
